import type {
  MotionAppliedChange,
  MotionLayerClipboardPayload,
  MotionStructuralApplyResult,
  MotionStructuralConnectorAction,
  MotionStructuralImpactReport,
} from "@blings/blings-player/editor-tools";

type DraftLayer = {
  ind?: number;
  uid?: number;
  nm?: string;
  ip?: number;
  op?: number;
  st?: number;
  parent?: number;
  refId?: string;
  tt?: number;
  tp?: number;
  ty?: number;
};
type JsonRecord = Record<string, unknown>;
type DraftAsset = {
  id?: string;
  nm?: string;
  layers?: DraftLayer[];
};
type DraftAnimation = {
  fr?: number;
  layers?: DraftLayer[];
  assets?: DraftAsset[];
  fonts?: { list?: unknown[] };
  chars?: unknown[];
  meta_b?: Record<string, unknown>;
};
type DraftLayerListInfo = {
  layers: DraftLayer[];
  path: string;
  assetId?: string;
};
type CreatedTarget = MotionStructuralApplyResult["createdTargets"][number];
type LayerTimingKey = "ip" | "op" | "st";

export type LayerClipboardPasteOptions = {
  currentFrame?: number;
};

export type LayerClipboardPasteResult = {
  animation: DraftAnimation;
  changes: MotionAppliedChange[];
  createdTargets: MotionStructuralApplyResult["createdTargets"];
  connectorActions: MotionStructuralConnectorAction[];
  impactReport: MotionStructuralImpactReport;
  importedAssetIds: string[];
};

const LAYER_CLIPBOARD_TYPE: MotionLayerClipboardPayload["type"] =
  "blings.motion.layers";
const STRUCTURAL_CONNECTOR_ACTION_KINDS = new Set([
  "willBreak",
  "addDuplicateTarget",
  "retargetMovedTarget",
]);
const STRUCTURAL_OPERATION_KINDS = new Set([
  "deleteLayers",
  "duplicateLayers",
  "copyLayers",
  "moveLayers",
  "groupLayersToPrecomp",
]);

function isRecord(value: unknown): value is JsonRecord {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function cloneClipboardValue<T>(value: T): T {
  if (value == null) {
    return value;
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecordArray(value: unknown): value is JsonRecord[] {
  return Array.isArray(value) && value.every(isRecord);
}

function isOptionalString(value: unknown): boolean {
  return value == null || typeof value === "string";
}

function isOptionalNumber(value: unknown): boolean {
  return value == null || typeof value === "number";
}

function isStructuralLayerReference(
  value: unknown
): value is MotionStructuralApplyResult["createdTargets"][number] {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.path === "string" &&
    typeof value.layerName === "string" &&
    isOptionalNumber(value.layerIndex) &&
    isOptionalString(value.assetId) &&
    isOptionalNumber(value.layerUid) &&
    isOptionalNumber(value.ind)
  );
}

function isStructuralLayerReferenceArray(value: unknown): boolean {
  return Array.isArray(value) && value.every(isStructuralLayerReference);
}

function isClipboardSource(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  return isOptionalString(value.projectId) && isOptionalString(value.sceneName);
}

function isMotionConnection(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.modIndex === "number" &&
    isRecord(value.valueSource)
  );
}

function isStructuralConnectorAction(
  value: unknown
): value is MotionStructuralConnectorAction {
  if (!isRecord(value)) {
    return false;
  }

  return (
    STRUCTURAL_CONNECTOR_ACTION_KINDS.has(value.kind as string) &&
    STRUCTURAL_OPERATION_KINDS.has(value.operation as string) &&
    isStructuralLayerReference(value.sourceTarget) &&
    (value.target == null || isStructuralLayerReference(value.target)) &&
    isMotionConnection(value.connection) &&
    typeof value.reason === "string" &&
    (value.clipboardSource == null || isClipboardSource(value.clipboardSource))
  );
}

function isStructuralConnectorActionArray(
  value: unknown
): value is MotionStructuralConnectorAction[] {
  return Array.isArray(value) && value.every(isStructuralConnectorAction);
}

function uniqueStrings(values: Array<string | undefined>): string[] {
  return Array.from(
    new Set(values.filter((value): value is string => !!value))
  );
}

export function createStructuralImpactReport(
  connectorActions: MotionStructuralConnectorAction[]
): MotionStructuralImpactReport {
  return {
    connectorActions,
    connectionIds: uniqueStrings(
      connectorActions.map((action) => action.connection.id)
    ),
    expressionConnectionIds: uniqueStrings(
      connectorActions
        .filter((action) => action.connection.valueSource.kind === "expression")
        .map((action) => action.connection.id)
    ),
    functionConnectionIds: uniqueStrings(
      connectorActions
        .filter((action) => action.connection.valueSource.kind === "function")
        .map((action) => action.connection.id)
    ),
    runtimeOnlyConnectionIds: uniqueStrings(
      connectorActions
        .filter(
          (action) => action.connection.valueSource.status === "runtime-only"
        )
        .map((action) => action.connection.id)
    ),
  };
}

function isStructuralImpactReport(
  value: unknown
): value is MotionStructuralImpactReport {
  if (!isRecord(value)) {
    return false;
  }

  const report = value as Partial<MotionStructuralImpactReport>;
  return (
    isStructuralConnectorActionArray(report.connectorActions) &&
    Array.isArray(report.connectionIds) &&
    Array.isArray(report.expressionConnectionIds) &&
    Array.isArray(report.functionConnectionIds) &&
    Array.isArray(report.runtimeOnlyConnectionIds)
  );
}

function isLayerClipboardGroup(
  value: unknown
): value is MotionLayerClipboardPayload["groups"][number] {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.sourcePath === "string" &&
    (value.sourceAssetId == null || typeof value.sourceAssetId === "string") &&
    isStructuralLayerReferenceArray(value.affectedTargets) &&
    isStructuralLayerReferenceArray(value.createdTargets) &&
    isRecordArray(value.layers)
  );
}

function isLayerClipboardPayload(
  value: unknown
): value is MotionLayerClipboardPayload {
  if (!isRecord(value)) {
    return false;
  }

  const payload = value as Partial<MotionLayerClipboardPayload>;
  return (
    payload.type === LAYER_CLIPBOARD_TYPE &&
    payload.version === 1 &&
    isRecord(payload.source) &&
    isRecord(payload.composition) &&
    isRecordArray(payload.layers) &&
    Array.isArray(payload.groups) &&
    payload.groups.every(isLayerClipboardGroup) &&
    isRecordArray(payload.assets) &&
    isStructuralLayerReferenceArray(payload.affectedTargets) &&
    isStructuralLayerReferenceArray(payload.createdTargets) &&
    isRecordArray(payload.pathChanges) &&
    isRecordArray(payload.idChanges) &&
    isStructuralConnectorActionArray(payload.connectorActions) &&
    (payload.impactReport == null ||
      isStructuralImpactReport(payload.impactReport)) &&
    Array.isArray(payload.importedAssetIds) &&
    Array.isArray(payload.warnings)
  );
}

export function parseLayerClipboardText(
  clipboardText?: string
): MotionLayerClipboardPayload | undefined {
  if (!clipboardText?.trim()) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(clipboardText);
    if (!isLayerClipboardPayload(parsed)) {
      return undefined;
    }

    return {
      ...parsed,
      impactReport:
        parsed.impactReport ??
        createStructuralImpactReport(parsed.connectorActions || []),
    };
  } catch {
    return undefined;
  }
}

function getDraftAssets(animation: DraftAnimation): DraftAsset[] {
  if (!Array.isArray(animation.assets)) {
    animation.assets = [];
  }
  return animation.assets;
}

function getAllDraftLayers(animation: DraftAnimation): DraftLayer[] {
  const rootLayers = Array.isArray(animation.layers) ? animation.layers : [];
  const assetLayers = getDraftAssets(animation).flatMap((asset) =>
    Array.isArray(asset.layers) ? asset.layers : []
  );
  return [...rootLayers, ...assetLayers];
}

function getNextLayerNumber(layers: DraftLayer[], key: "ind" | "uid"): number {
  return (
    layers.reduce((maxValue, layer) => {
      const value = layer[key];
      return typeof value === "number" ? Math.max(maxValue, value) : maxValue;
    }, 0) + 1
  );
}

function getUniqueName(
  existingNames: Set<string>,
  preferredName?: string
): string | undefined {
  if (!preferredName) {
    return preferredName;
  }

  if (!existingNames.has(preferredName)) {
    existingNames.add(preferredName);
    return preferredName;
  }

  let suffix = 2;
  let candidate = `${preferredName} ${suffix}`;
  while (existingNames.has(candidate)) {
    suffix += 1;
    candidate = `${preferredName} ${suffix}`;
  }
  existingNames.add(candidate);
  return candidate;
}

function getUniqueAssetId(
  existingAssetIds: Set<string>,
  preferredId?: string
): string {
  const baseId = preferredId || "asset";
  if (!existingAssetIds.has(baseId)) {
    existingAssetIds.add(baseId);
    return baseId;
  }

  let suffix = 2;
  let candidate = `${baseId}_${suffix}`;
  while (existingAssetIds.has(candidate)) {
    suffix += 1;
    candidate = `${baseId}_${suffix}`;
  }
  existingAssetIds.add(candidate);
  return candidate;
}

function getLayerListByClipboardGroup(
  animation: DraftAnimation,
  group: MotionLayerClipboardPayload["groups"][number]
): DraftLayerListInfo {
  const rootLayers = animation.layers || (animation.layers = []);
  const assets = getDraftAssets(animation);

  if (group.sourceAssetId != null) {
    const asset = assets.find(
      (candidate) => candidate.id === group.sourceAssetId
    );
    if (asset && Array.isArray(asset.layers)) {
      const assetIndex = assets.indexOf(asset);
      return {
        layers: asset.layers,
        path: `assets[${assetIndex}].layers`,
        assetId: asset.id,
      };
    }

    return { layers: rootLayers, path: "layers" };
  }

  const assetIndexBySourcePath = /^assets\[(\d+)\]\.layers$/.exec(
    group.sourcePath
  )?.[1];
  if (assetIndexBySourcePath != null) {
    const asset = assets[Number(assetIndexBySourcePath)];
    if (asset && Array.isArray(asset.layers)) {
      return {
        layers: asset.layers,
        path: `assets[${assetIndexBySourcePath}].layers`,
        assetId: asset.id,
      };
    }
  }

  return { layers: rootLayers, path: "layers" };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function getPasteTimingDelta(
  layers: DraftLayer[],
  currentFrame?: number
): number | undefined {
  if (!isFiniteNumber(currentFrame)) {
    return undefined;
  }

  const startFrames = layers
    .map((layer) => (isFiniteNumber(layer.ip) ? layer.ip : layer.st))
    .filter(isFiniteNumber);
  if (!startFrames.length) {
    return undefined;
  }

  return currentFrame - Math.min(...startFrames);
}

function shiftLayerTiming(layers: DraftLayer[], delta?: number): void {
  if (!isFiniteNumber(delta) || delta === 0) {
    return;
  }

  const timingKeys: LayerTimingKey[] = ["ip", "op", "st"];
  layers.forEach((layer) => {
    timingKeys.forEach((key) => {
      if (isFiniteNumber(layer[key])) {
        layer[key] += delta;
      }
    });
  });
}

function shiftKeyframeTimes(
  value: unknown,
  delta: number,
  seen: WeakSet<object>
): void {
  if (!value || typeof value !== "object") {
    return;
  }

  if (seen.has(value)) {
    return;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item) => shiftKeyframeTimes(item, delta, seen));
    return;
  }

  const record = value as JsonRecord;
  if (Array.isArray(record.k)) {
    record.k.forEach((keyframe) => {
      if (!isRecord(keyframe) || !isFiniteNumber(keyframe.t)) {
        return;
      }

      keyframe.t += delta;
    });
  }

  Object.values(record).forEach((child) =>
    shiftKeyframeTimes(child, delta, seen)
  );
}

function shiftLayerTimelineData(layers: DraftLayer[], delta?: number): void {
  if (!isFiniteNumber(delta) || delta === 0) {
    return;
  }

  shiftLayerTiming(layers, delta);
  const seen = new WeakSet<object>();
  layers.forEach((layer) => shiftKeyframeTimes(layer, delta, seen));
}

function createRootChange(
  kind: MotionAppliedChange["kind"],
  path: string,
  value: unknown
): MotionAppliedChange {
  return {
    kind,
    layerName: "",
    path,
    rootPath: path,
    serializedValue: cloneClipboardValue(
      value
    ) as MotionAppliedChange["serializedValue"],
    debug: { isAnimated: false },
  };
}

function mergeUniqueValues<T>(current: T[] | undefined, incoming?: T[]): T[] {
  const nextValues = [...(current || [])];
  const seen = new Set(nextValues.map((value) => JSON.stringify(value)));

  (incoming || []).forEach((value) => {
    const key = JSON.stringify(value);
    if (!seen.has(key)) {
      seen.add(key);
      nextValues.push(cloneClipboardValue(value));
    }
  });

  return nextValues;
}

function mergeClipboardDocumentData(
  animation: DraftAnimation,
  payload: MotionLayerClipboardPayload
): void {
  if (payload.fonts?.list?.length) {
    animation.fonts = animation.fonts || { list: [] };
    animation.fonts.list = mergeUniqueValues(
      animation.fonts.list,
      payload.fonts.list
    );
  }

  if (payload.chars?.length) {
    animation.chars = mergeUniqueValues(animation.chars, payload.chars);
  }

  if (payload.meta_b) {
    animation.meta_b = animation.meta_b || {};
    Object.entries(payload.meta_b).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const current = Array.isArray(animation.meta_b?.[key])
          ? (animation.meta_b?.[key] as unknown[])
          : [];
        animation.meta_b![key] = mergeUniqueValues(current, value);
        return;
      }

      if (animation.meta_b && animation.meta_b[key] == null) {
        animation.meta_b[key] = cloneClipboardValue(value);
      }
    });
  }
}

function applyAssetIdRemaps(
  layer: DraftLayer,
  assetIdRemaps: Map<string, string>
): void {
  if (layer.refId && assetIdRemaps.has(layer.refId)) {
    layer.refId = assetIdRemaps.get(layer.refId);
  }
}

function remapLayerUids(layers: DraftLayer[], takeUid: () => number): void {
  layers.forEach((layer) => {
    layer.uid = takeUid();
  });
}

function remapInsertedLayers(
  layers: DraftLayer[],
  destination: DraftLayerListInfo,
  takeUid: () => number,
  assetIdRemaps: Map<string, string>
): void {
  const indRemaps = new Map<number, number>();
  let nextInd = getNextLayerNumber(destination.layers, "ind");
  const existingNames = new Set(
    destination.layers
      .map((layer) => layer.nm)
      .filter((name): name is string => !!name)
  );

  layers.forEach((layer) => {
    const previousInd = layer.ind;
    const nextLayerInd = nextInd;
    nextInd += 1;

    if (typeof previousInd === "number") {
      indRemaps.set(previousInd, nextLayerInd);
    }

    layer.ind = nextLayerInd;
    layer.uid = takeUid();
    layer.nm = getUniqueName(existingNames, layer.nm);
    applyAssetIdRemaps(layer, assetIdRemaps);
  });

  layers.forEach((layer) => {
    if (typeof layer.parent === "number" && indRemaps.has(layer.parent)) {
      layer.parent = indRemaps.get(layer.parent);
    }
    if (typeof layer.tp === "number" && indRemaps.has(layer.tp)) {
      layer.tp = indRemaps.get(layer.tp);
    }
  });
}

function getLayerReference(
  listInfo: DraftLayerListInfo,
  layer: DraftLayer,
  index: number
): CreatedTarget {
  return {
    path: `${listInfo.path}[${index}]`,
    ...(listInfo.assetId ? { assetId: listInfo.assetId } : {}),
    layerName: layer.nm || `Layer ${layer.ind ?? index}`,
    layerUid: layer.uid,
    layerIndex: index,
    ind: layer.ind,
  };
}

function remapConnectorActions(
  actions: MotionStructuralConnectorAction[],
  createdTargetRemaps: Map<string, CreatedTarget>
): MotionStructuralConnectorAction[] {
  return actions.map((action) => {
    if (!action.target) {
      return action;
    }

    const nextTarget =
      (typeof action.target.layerUid === "number"
        ? createdTargetRemaps.get(`uid:${action.target.layerUid}`)
        : undefined) ??
      (action.target.path
        ? createdTargetRemaps.get(`path:${action.target.path}`)
        : undefined);

    return nextTarget ? { ...action, target: nextTarget } : action;
  });
}

export function prepareLayerClipboardPaste(
  animation: unknown,
  payload: MotionLayerClipboardPayload,
  options: LayerClipboardPasteOptions = {}
): LayerClipboardPasteResult | undefined {
  if (!payload.layers.length) {
    return undefined;
  }

  const nextJson = cloneClipboardValue(animation) as DraftAnimation;
  const nextAssets = getDraftAssets(nextJson);
  const existingAssetIds = new Set(
    nextAssets.map((asset) => asset.id).filter((id): id is string => !!id)
  );
  const assetIdRemaps = new Map<string, string>();
  let nextUid = getNextLayerNumber(getAllDraftLayers(nextJson), "uid");
  const takeUid = () => {
    const uid = nextUid;
    nextUid += 1;
    return uid;
  };

  const importedAssets = cloneClipboardValue(
    payload.assets || []
  ) as DraftAsset[];
  importedAssets.forEach((asset) => {
    const previousId = asset.id;
    const nextId = getUniqueAssetId(existingAssetIds, previousId);
    if (previousId && previousId !== nextId) {
      assetIdRemaps.set(previousId, nextId);
    }
    asset.id = nextId;
  });
  importedAssets.forEach((asset) => {
    if (Array.isArray(asset.layers)) {
      remapLayerUids(asset.layers, takeUid);
      asset.layers.forEach((layer) => applyAssetIdRemaps(layer, assetIdRemaps));
    }
  });

  if (importedAssets.length) {
    nextAssets.push(...importedAssets);
  }
  mergeClipboardDocumentData(nextJson, payload);

  const createdTargets: MotionStructuralApplyResult["createdTargets"] = [];
  const createdTargetRemaps = new Map<string, CreatedTarget>();
  const changes = importedAssets.length
    ? [createRootChange("asset", "assets", nextAssets)]
    : [];
  const timingDelta = getPasteTimingDelta(
    payload.groups.flatMap((group) => group.layers),
    options.currentFrame
  );
  const pasteInsertOffsetsByPath = new Map<string, number>();

  payload.groups.forEach((group) => {
    if (!group.layers.length) {
      return;
    }

    const destination = getLayerListByClipboardGroup(nextJson, group);
    const pastedLayers = cloneClipboardValue(group.layers) as DraftLayer[];
    const insertIndex = pasteInsertOffsetsByPath.get(destination.path) ?? 0;

    shiftLayerTimelineData(pastedLayers, timingDelta);
    remapInsertedLayers(pastedLayers, destination, takeUid, assetIdRemaps);
    destination.layers.splice(insertIndex, 0, ...pastedLayers);
    pasteInsertOffsetsByPath.set(
      destination.path,
      insertIndex + pastedLayers.length
    );

    pastedLayers.forEach((layer, offset) => {
      const createdTarget = getLayerReference(
        destination,
        layer,
        insertIndex + offset
      );
      createdTargets.push(createdTarget);

      const clipboardTarget = group.createdTargets[offset];
      if (typeof clipboardTarget?.layerUid === "number") {
        createdTargetRemaps.set(
          `uid:${clipboardTarget.layerUid}`,
          createdTarget
        );
      }
      if (clipboardTarget?.path) {
        createdTargetRemaps.set(`path:${clipboardTarget.path}`, createdTarget);
      }
    });

    changes.push(
      createRootChange("duplicateLayers", destination.path, destination.layers)
    );
  });

  if (!createdTargets.length) {
    return undefined;
  }

  const connectorActions = remapConnectorActions(
    payload.connectorActions || [],
    createdTargetRemaps
  );

  return {
    animation: nextJson,
    changes,
    createdTargets,
    connectorActions,
    impactReport: createStructuralImpactReport(connectorActions),
    importedAssetIds: importedAssets
      .map((asset) => asset.id)
      .filter((id): id is string => !!id),
  };
}
